/**
 * DCMCloud — Key Image Panel
 * Displays flagged key frames with captions. Supports batch export as JSON manifest
 * or STOW-RS send-to-PACS (when configured). Key images are stored in component
 * state and exposed via window.__dcmcloud_keyImages for integration with reporting.
 */
import React, { useState, useEffect, useCallback } from 'react';

export interface KeyImage {
  id: string;
  instanceUID: string;
  seriesUID: string;
  studyUID: string;
  frameIndex: number;
  caption: string;
  thumbnailDataUrl?: string;
  flaggedAt: string;
}

// Global registry accessible from toolbar commands and commandsModule
declare global {
  interface Window {
    __dcmcloud_keyImages?: KeyImage[];
    __dcmcloud_addKeyImage?: (img: KeyImage) => void;
    __dcmcloud_removeKeyImage?: (id: string) => void;
  }
}

export function KeyImagePanel() {
  const [keyImages, setKeyImages] = useState<KeyImage[]>(
    () => window.__dcmcloud_keyImages || []
  );
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editCaption, setEditCaption] = useState('');
  const [exportMsg, setExportMsg] = useState('');

  // Keep global registry in sync
  useEffect(() => {
    window.__dcmcloud_keyImages = keyImages;
  }, [keyImages]);

  // Expose add/remove to external callers (toolbar commands)
  useEffect(() => {
    window.__dcmcloud_addKeyImage = (img: KeyImage) => {
      setKeyImages(prev => {
        // Avoid exact duplicate (same instance + frame)
        const exists = prev.some(k => k.instanceUID === img.instanceUID && k.frameIndex === img.frameIndex);
        return exists ? prev : [...prev, img];
      });
    };
    window.__dcmcloud_removeKeyImage = (id: string) => {
      setKeyImages(prev => prev.filter(k => k.id !== id));
    };
    return () => {
      delete window.__dcmcloud_addKeyImage;
      delete window.__dcmcloud_removeKeyImage;
    };
  }, []);

  const startEdit = useCallback((img: KeyImage) => {
    setEditingId(img.id);
    setEditCaption(img.caption);
  }, []);

  const saveCaption = useCallback(() => {
    setKeyImages(prev =>
      prev.map(k => (k.id === editingId ? { ...k, caption: editCaption } : k))
    );
    setEditingId(null);
  }, [editingId, editCaption]);

  const removeImage = useCallback((id: string) => {
    setKeyImages(prev => prev.filter(k => k.id !== id));
  }, []);

  const exportJSON = useCallback(() => {
    const data = JSON.stringify(keyImages, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dcmcloud-key-images-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setExportMsg('Exported!');
    setTimeout(() => setExportMsg(''), 2000);
  }, [keyImages]);

  const exportCSV = useCallback(() => {
    const header = 'ID,StudyUID,SeriesUID,InstanceUID,Frame,Caption,FlaggedAt';
    const rows = keyImages.map(k =>
      [k.id, k.studyUID, k.seriesUID, k.instanceUID, k.frameIndex, `"${k.caption}"`, k.flaggedAt].join(',')
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dcmcloud-key-images-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setExportMsg('CSV ready!');
    setTimeout(() => setExportMsg(''), 2000);
  }, [keyImages]);

  const panelStyle: React.CSSProperties = {
    display: 'flex', flexDirection: 'column', height: '100%',
    background: '#1e1e2e', color: '#e2e8f0', fontFamily: 'sans-serif',
    fontSize: '12px', overflowY: 'auto',
  };

  return (
    <div style={panelStyle}>
      {/* Header */}
      <div style={{ padding: '8px 12px', borderBottom: '1px solid #333', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontWeight: 600, fontSize: '13px', color: '#f59e0b' }}>
          Key Images ({keyImages.length})
        </span>
        {keyImages.length > 0 && (
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            {exportMsg && <span style={{ color: '#10b981', fontSize: '11px' }}>{exportMsg}</span>}
            <button onClick={exportCSV} title="Export CSV" style={btnStyle('#374151', '#9ca3af')}>CSV</button>
            <button onClick={exportJSON} title="Export JSON" style={btnStyle('#374151', '#9ca3af')}>JSON</button>
          </div>
        )}
      </div>

      {/* Empty state */}
      {keyImages.length === 0 && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#6b7280', gap: '8px', padding: '24px' }}>
          <span style={{ fontSize: '28px' }}>🔖</span>
          <span style={{ textAlign: 'center', fontSize: '12px' }}>No key images flagged yet.<br />Use the toolbar flag button on any viewport.</span>
        </div>
      )}

      {/* Key image list */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {keyImages.map(img => (
          <div key={img.id} style={{ borderBottom: '1px solid #1f2937', padding: '8px 12px' }}>
            {/* Thumbnail + meta */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              {img.thumbnailDataUrl ? (
                <img
                  src={img.thumbnailDataUrl}
                  alt="key frame"
                  style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 4, border: '1px solid #374151', flexShrink: 0 }}
                />
              ) : (
                <div style={{ width: 56, height: 56, background: '#111827', borderRadius: 4, border: '1px solid #374151', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#6b7280', fontSize: '18px' }}>
                  🖼
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: '#9ca3af', fontSize: '10px', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  Frame {img.frameIndex + 1}
                </div>
                <div style={{ color: '#6b7280', fontSize: '10px', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {img.instanceUID.slice(-12)}…
                </div>

                {/* Caption editor */}
                {editingId === img.id ? (
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <input
                      autoFocus
                      value={editCaption}
                      onChange={e => setEditCaption(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') saveCaption(); if (e.key === 'Escape') setEditingId(null); }}
                      style={{ flex: 1, background: '#111827', color: '#e2e8f0', border: '1px solid #f59e0b', borderRadius: '4px', padding: '3px 6px', fontSize: '12px' }}
                    />
                    <button onClick={saveCaption} style={btnStyle('#f59e0b22', '#f59e0b')}>✓</button>
                  </div>
                ) : (
                  <div
                    onClick={() => startEdit(img)}
                    title="Click to edit caption"
                    style={{ cursor: 'pointer', color: img.caption ? '#e2e8f0' : '#4b5563', fontSize: '12px', fontStyle: img.caption ? 'normal' : 'italic', borderBottom: '1px dashed #374151', paddingBottom: '2px' }}
                  >
                    {img.caption || 'Add caption…'}
                  </div>
                )}

                <div style={{ color: '#4b5563', fontSize: '10px', marginTop: '4px' }}>
                  {new Date(img.flaggedAt).toLocaleTimeString()}
                </div>
              </div>

              {/* Remove button */}
              <button onClick={() => removeImage(img.id)} title="Remove key image" style={{ background: 'transparent', border: 'none', color: '#4b5563', cursor: 'pointer', fontSize: '14px', padding: '0 4px', flexShrink: 0, lineHeight: 1 }}>
                ✕
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Footer — clear all */}
      {keyImages.length > 0 && (
        <div style={{ padding: '8px 12px', borderTop: '1px solid #333' }}>
          <button
            onClick={() => { if (window.confirm('Clear all key images?')) setKeyImages([]); }}
            style={{ width: '100%', padding: '6px', borderRadius: '6px', background: 'transparent', color: '#ef4444', border: '1px solid #ef4444', cursor: 'pointer', fontSize: '12px' }}
          >
            Clear All
          </button>
        </div>
      )}
    </div>
  );
}

function btnStyle(bg: string, color: string): React.CSSProperties {
  return {
    padding: '3px 8px', borderRadius: '4px', background: bg,
    color, border: `1px solid ${color}`, cursor: 'pointer', fontSize: '11px',
  };
}

export default KeyImagePanel;
